import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fetchApi } from "../../utils/api";
import { getAuthToken } from "../../services/authToken";
import { ProposalView, RoleplayTab } from "./DemoSetupJobViewer";

interface DemoSetupForm {
  url: string;
  tenant_name: string;
  location_name: string;
  timezone: string;
  server_pin: string;
}

interface JobStatus {
  status: "pending" | "processing" | "complete" | "error";
  progress_message: string | null;
  proposal_json: any | null;
  insert_sql: string | null;
  delete_sql: string | null;
  roleplay_script: string | null;
  warnings: string[] | null;
  error_message: string | null;
  tenant_id: string | null;
  executed_at: string | null;
}

const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Phoenix",
  "Pacific/Honolulu",
];

const DemoSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState<DemoSetupForm>({
    url: "",
    tenant_name: "",
    location_name: "",
    timezone: "America/Los_Angeles",
    server_pin: "1234",
  });
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [activeTab, setActiveTab] = useState<"proposal" | "insert" | "delete" | "roleplay">("proposal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [executeResult, setExecuteResult] = useState<{ success: boolean; message: string } | null>(null);
  const [cleanupResult, setCleanupResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getAuthHeaders = () => {
    if (user?.accessToken) {
      return { Authorization: `Bearer ${user.accessToken}` };
    }
    const token = getAuthToken();
    if (!token) throw new Error("Authentication required. Please log in again.");
    return { Authorization: `Bearer ${token}` };
  };

  // Poll for job status
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const status = await fetchApi<JobStatus>(`/api/admin/demo-setup/status/${jobId}`, {
          headers: getAuthHeaders(),
        });
        setJobStatus(status);

        if (status.status === "complete" || status.status === "error") {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (e: any) {
        console.error("Poll error:", e);
      }
    };

    poll(); // immediate first poll
    pollRef.current = setInterval(poll, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    setJobStatus(null);
    setActiveTab("proposal");

    try {
      const result = await fetchApi<{ job_id: string }>("/api/admin/demo-setup/generate", {
        method: "POST",
        headers: getAuthHeaders(),
        body: form,
      });
      setJobId(result.job_id);
    } catch (e: any) {
      setError(e.message || "Failed to start demo setup generation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExecute = async () => {
    if (!jobId || !window.confirm("Execute INSERT SQL against the database? This will create a new tenant with all demo data.")) return;
    setIsExecuting(true);
    setError(null);
    setExecuteResult(null);
    try {
      const result = await fetchApi<{ status: string; tenant_id: string }>(`/api/admin/demo-setup/execute/${jobId}`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      // Re-fetch status to get tenant_id and executed_at
      const status = await fetchApi<JobStatus>(`/api/admin/demo-setup/status/${jobId}`, {
        headers: getAuthHeaders(),
      });
      setJobStatus(status);
      setExecuteResult({ success: true, message: `Tenant created successfully! Tenant ID: ${result.tenant_id}` });
    } catch (e: any) {
      setExecuteResult({ success: false, message: e.message || "Failed to execute SQL" });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCleanup = async () => {
    if (!jobId || !window.confirm("Run DELETE SQL? This will remove the demo tenant and all its data.")) return;
    setIsCleaning(true);
    setError(null);
    setCleanupResult(null);
    try {
      await fetchApi(`/api/admin/demo-setup/cleanup/${jobId}`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const status = await fetchApi<JobStatus>(`/api/admin/demo-setup/status/${jobId}`, {
        headers: getAuthHeaders(),
      });
      setJobStatus(status);
      setCleanupResult({ success: true, message: "Demo tenant cleaned up successfully." });
      setExecuteResult(null);
    } catch (e: any) {
      setCleanupResult({ success: false, message: e.message || "Failed to run cleanup" });
    } finally {
      setIsCleaning(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch {
      setCopySuccess("Failed to copy");
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  const isLoading = jobStatus && (jobStatus.status === "pending" || jobStatus.status === "processing");
  const isComplete = jobStatus?.status === "complete";
  const isError = jobStatus?.status === "error";
  const isExecuted = !!jobStatus?.executed_at;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Generate Demo Tenant</h1>
            <p className="text-gray-600 mt-1">
              Enter a restaurant's website URL to auto-generate a complete demo configuration.
            </p>
          </div>
          <button
            onClick={() => navigate("/tenant_management")}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Back to Tenant Management
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
            <button className="ml-2 font-bold" onClick={() => setError(null)}>x</button>
          </div>
        )}

        {/* Form */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2 font-medium">
                  Restaurant Website URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://www.restaurant.com"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2 font-medium">
                  Tenant Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.tenant_name}
                  onChange={(e) => setForm((p) => ({ ...p, tenant_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="The Restaurant"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2 font-medium">Location Name</label>
                <input
                  type="text"
                  value={form.location_name}
                  onChange={(e) => setForm((p) => ({ ...p, location_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Defaults to 'Tenant Name - Main'"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2 font-medium">Timezone</label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 mb-2 font-medium">Server PIN</label>
                <input
                  type="text"
                  value={form.server_pin}
                  onChange={(e) => setForm((p) => ({ ...p, server_pin: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1234"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting || !!isLoading}
              className={`bg-blue-600 text-white px-6 py-2 rounded font-medium hover:bg-blue-700 transition-colors ${
                isSubmitting || isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? "Starting..." : isLoading ? "Generating..." : "Generate Demo Setup"}
            </button>
          </form>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="bg-white shadow-md rounded-lg p-8 mb-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-700 font-medium">
              {jobStatus?.progress_message || "Starting..."}
            </p>
            <p className="mt-1 text-gray-500 text-sm">This usually takes 30-60 seconds</p>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h3 className="text-red-800 font-semibold mb-2">Generation Failed</h3>
            <p className="text-red-700">{jobStatus?.error_message}</p>
          </div>
        )}

        {/* Results */}
        {isComplete && jobStatus && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {/* Warnings */}
            {jobStatus.warnings && jobStatus.warnings.length > 0 && (
              <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
                <p className="text-yellow-800 font-medium text-sm">Warnings:</p>
                {jobStatus.warnings.map((w, i) => (
                  <p key={i} className="text-yellow-700 text-sm ml-2">- {w}</p>
                ))}
              </div>
            )}

            {/* Execution status */}
            {isExecuted && (
              <div className="bg-green-50 border-b border-green-200 px-6 py-3">
                <p className="text-green-800 font-medium text-sm">
                  SQL executed successfully! Tenant ID: {jobStatus.tenant_id}
                  {" "}
                  <button
                    onClick={() => navigate("/tenant_management")}
                    className="text-green-700 underline hover:text-green-900"
                  >
                    Go to Tenant Management
                  </button>
                </p>
              </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {([
                  ["proposal", "Proposal"],
                  ["insert", "Insert SQL"],
                  ["delete", "Delete SQL"],
                  ["roleplay", "Roleplay Script"],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === key
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab content */}
            <div className="p-6">
              {/* Copy success toast */}
              {copySuccess && (
                <div className="fixed top-4 right-4 bg-gray-800 text-white px-4 py-2 rounded shadow-lg z-50 text-sm">
                  {copySuccess === "Failed to copy" ? copySuccess : `Copied ${copySuccess}!`}
                </div>
              )}

              {activeTab === "proposal" && jobStatus.proposal_json && (
                <ProposalView proposal={jobStatus.proposal_json} />
              )}

              {activeTab === "insert" && jobStatus.insert_sql && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <button
                      onClick={() => copyToClipboard(jobStatus.insert_sql!, "Insert SQL")}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      Copy to Clipboard
                    </button>
                    <button
                      onClick={handleExecute}
                      disabled={isExecuting || isExecuted}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        isExecuted
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : isExecuting
                          ? "bg-green-600 text-white opacity-50 cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700"
                      }`}
                    >
                      {isExecuting ? "Executing..." : isExecuted ? "Executed" : "Execute SQL"}
                    </button>
                  </div>
                  {executeResult && (
                    <div className={`mb-4 px-4 py-3 rounded text-sm ${
                      executeResult.success
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}>
                      <p className="font-medium">{executeResult.success ? "Success" : "Error"}</p>
                      <p>{executeResult.message}</p>
                      {executeResult.success && (
                        <button
                          onClick={() => navigate("/tenant_management")}
                          className="mt-2 text-green-700 underline hover:text-green-900 text-sm"
                        >
                          Go to Tenant Management
                        </button>
                      )}
                    </div>
                  )}
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-[600px] overflow-y-auto">
                    {jobStatus.insert_sql}
                  </pre>
                </div>
              )}

              {activeTab === "delete" && jobStatus.delete_sql && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <button
                      onClick={() => copyToClipboard(jobStatus.delete_sql!, "Delete SQL")}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      Copy to Clipboard
                    </button>
                    {isExecuted && (
                      <button
                        onClick={handleCleanup}
                        disabled={isCleaning}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                          isCleaning
                            ? "bg-red-600 text-white opacity-50 cursor-not-allowed"
                            : "bg-red-600 text-white hover:bg-red-700"
                        }`}
                      >
                        {isCleaning ? "Cleaning up..." : "Run Cleanup"}
                      </button>
                    )}
                  </div>
                  {cleanupResult && (
                    <div className={`mb-4 px-4 py-3 rounded text-sm ${
                      cleanupResult.success
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}>
                      <p className="font-medium">{cleanupResult.success ? "Success" : "Error"}</p>
                      <p>{cleanupResult.message}</p>
                    </div>
                  )}
                  <pre className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed max-h-[600px] overflow-y-auto">
                    {jobStatus.delete_sql}
                  </pre>
                </div>
              )}

              {activeTab === "roleplay" && (
                <RoleplayTab
                  roleplayScript={jobStatus.roleplay_script}
                  learnUnits={jobStatus.proposal_json?.learn_units || []}
                  onCopy={copyToClipboard}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoSetup;
